import { useSearchParams } from 'react-router-dom';

export interface GuestDetectionResult {
  isGuest: boolean;
  inviteCode?: string;
  sessionId?: string;
  guestName?: string;
}

/**
 * Detects if a user is joining as a guest via invite code or session URL
 */
export function useGuestDetection(): GuestDetectionResult {
  const [searchParams] = useSearchParams();
  
  // Check for invite code in URL parameters
  const inviteCode = searchParams.get('invite') || searchParams.get('code');
  
  // Check for session ID in URL parameters
  const sessionId = searchParams.get('session') || searchParams.get('sessionId');
  
  // Check for guest name in URL parameters
  const guestName = searchParams.get('guest') || searchParams.get('guestName');
  
  // Check if current path indicates guest access
  const currentPath = window.location.pathname;
  const isGuestPath = currentPath.includes('/join') || 
                     currentPath.includes('/session/') ||
                     currentPath.includes('/guest');
  
  // Determine if this is a guest user
  const isGuest = !!(inviteCode || sessionId || guestName || isGuestPath);
  
  return {
    isGuest,
    inviteCode: inviteCode || undefined,
    sessionId: sessionId || undefined,
    guestName: guestName || undefined
  };
}

/**
 * Stores guest status in session storage for persistence across navigation
 */
export function setGuestStatus(guestData: GuestDetectionResult): void {
  try {
    sessionStorage.setItem('deepFocus_guestStatus', JSON.stringify({
      ...guestData,
      timestamp: Date.now()
    }));
  } catch (error) {
    console.warn('Failed to store guest status:', error);
  }
}

/**
 * Retrieves guest status from session storage
 */
export function getGuestStatus(): GuestDetectionResult | null {
  try {
    const stored = sessionStorage.getItem('deepFocus_guestStatus');
    if (!stored) return null;
    
    const parsed = JSON.parse(stored);

    if (typeof parsed.timestamp !== 'number') {
      sessionStorage.removeItem('deepFocus_guestStatus');
      return null;
    }
    
    // Check if guest status is still valid (within 1 hour)
    const oneHour = 60 * 60 * 1000;
    if (Date.now() - parsed.timestamp > oneHour) {
      sessionStorage.removeItem('deepFocus_guestStatus');
      return null;
    }
    
    return {
      isGuest: parsed.isGuest,
      inviteCode: parsed.inviteCode,
      sessionId: parsed.sessionId,
      guestName: parsed.guestName
    };
  } catch (error) {
    console.warn('Failed to retrieve guest status:', error);
    return null;
  }
}

/**
 * Clears guest status from session storage
 */
export function clearGuestStatus(): void {
  try {
    sessionStorage.removeItem('deepFocus_guestStatus');
  } catch (error) {
    console.warn('Failed to clear guest status:', error);
  }
}

/**
 * Checks if a URL contains guest indicators
 */
export function isGuestUrl(url: string): boolean {
  try {
    const urlObj = new URL(url);
    const params = urlObj.searchParams;
    
    return !!(params.get('invite') || 
             params.get('code') || 
             params.get('session') || 
             params.get('sessionId') ||
             params.get('guest') ||
             params.get('guestName') ||
             urlObj.pathname.includes('/join') ||
             urlObj.pathname.includes('/session/') ||
             urlObj.pathname.includes('/guest'));
  } catch {
    return false;
  }
}
